import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useCallback, useEffect } from 'react'
import {
    defaultSuggestionCardFilter,
    TRACK_CHANGE_STATUS,
    TRACK_CHANGE_STATUS_MAP,
    TRACK_CHANGE_STATUS_VALUES,
} from '../constants'
import { useSuggestionDataHandler } from './useSuggestionDataHandler'
import { useTrackChangeCards } from './useTrackChangeCards'

/**
 * Custom hook to handle suggestion card filtering based on the current filter state.
 * @returns {Object} An object containing the processSuggestionData function.
 */
export const useSuggestionCardFilterHandler = () => {
    const { renderSuggestions } = useSuggestionDataHandler()
    const { debouncedUpdateCardPositions } = useTrackChangeCards()
    const {
        isLoadingCards,
        setIsLoadingCards,
        isSuggestionsDataUpdated,
        suggestionCardFilter,
    } = useSuggestionsShallowSelector((s) => ({
        isLoadingCards: s.isLoadingCards,
        setIsLoadingCards: s.setIsLoadingCards,
        isSuggestionsDataUpdated: s.isSuggestionsDataUpdated,
        suggestionCardFilter: s.suggestionCardFilter,
    }))

    const processSuggestionData = useCallback(
        async (resolutionType?: string) => {
            const resolvedSuggestionCardFilter =
                resolutionType || suggestionCardFilter

            switch (resolvedSuggestionCardFilter) {
                case TRACK_CHANGE_STATUS_VALUES.RESOLVED:
                    renderSuggestions(
                        TRACK_CHANGE_STATUS_MAP[
                            TRACK_CHANGE_STATUS_VALUES.RESOLVED
                        ],
                        undefined
                    )
                    break
                case TRACK_CHANGE_STATUS_VALUES.OPEN:
                    renderSuggestions(
                        TRACK_CHANGE_STATUS_MAP[
                            TRACK_CHANGE_STATUS_VALUES.OPEN
                        ],
                        TRACK_CHANGE_STATUS.PENDING
                    )
                    break
                case defaultSuggestionCardFilter:
                    await debouncedUpdateCardPositions()
                    break
                default:
                    break
            }
        },
        [suggestionCardFilter, renderSuggestions, debouncedUpdateCardPositions]
    )

    // React to changes in isSuggestionsDataUpdated and isLoadingCards
    useEffect(() => {
        const shouldProcess = !isSuggestionsDataUpdated && isLoadingCards
        if (shouldProcess) {
            processSuggestionData().finally(() => setIsLoadingCards(false))
        }
    }, [
        isSuggestionsDataUpdated,
        processSuggestionData,
        isLoadingCards,
        setIsLoadingCards,
    ])

    return {
        processSuggestionData,
    }
}
